import { useEffect, useState } from "react";
import moment from "moment-timezone";
import Modal from "react-modal";
import { useIntl } from "react-intl";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwButton from "../../../shared/OwButton/OwButton";
import OwBadge from "../../../shared/OwBadge/OwBadge";
import { LoadingResults } from "../../../shared/LoadingResults/LoadingResults";
import { useAppContextNonNull } from "../../../../contexts/AppContext";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import ISupplier_Delivery_Schedule from "../../../../models/ISupplier_Delivery_Schedule";
import ISupplier_Delivery_Schedule_Override from "../../../../models/ISupplier_Delivery_Schedule_Override";
import DeliveryDeadlinesService from "../../../../services/DeliveryDeadlinesService";
import DeliveryScheduleOverrideForm from "./DeliveryScheduleOverrideForm";
import { format12h } from "./deliveryDeadlineUtils";

type Props = {
    schedules: ISupplier_Delivery_Schedule[];
};

type EditTarget = { mode: "new" } | { mode: "edit"; override: ISupplier_Delivery_Schedule_Override } | null;

/**
 * "Schedule exceptions" block on the Delivery Deadlines settings page: the upcoming holiday-week
 * overrides (no deliveries, or replacement delivery dates) for one template or all of them, with
 * add / edit / delete. Past weeks are hidden; exceptions expire on their own.
 */
export const DeliveryScheduleOverridesSection = (props: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();
    const intl = useIntl();

    const [overrides, setOverrides] = useState<ISupplier_Delivery_Schedule_Override[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [target, setTarget] = useState<EditTarget>(null);
    const [pendingDelete, setPendingDelete] = useState<ISupplier_Delivery_Schedule_Override | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const loadOverrides = async () => {
        setIsLoading(true);
        try {
            const data = await DeliveryDeadlinesService.getDeliveryScheduleOverrides(
                userModel,
                supplierModel,
                moment().format("YYYY-MM-DD")
            );
            setOverrides(data.deliveryScheduleOverrides);
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadOverrides();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSaved = async () => {
        setTarget(null);
        await loadOverrides();
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setIsDeleting(true);
        try {
            await DeliveryDeadlinesService.deleteDeliveryScheduleOverride(pendingDelete.guid, userModel, supplierModel);
            uiContext.showNotification({ message: "schedule_exception_deleted", level: NotificationLevelEnum.success });
            setPendingDelete(null);
            await loadOverrides();
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsDeleting(false);
        }
    };

    const weekLabel = (override: ISupplier_Delivery_Schedule_Override): string => {
        const start = moment(String(override.week_start).slice(0, 10), "YYYY-MM-DD");
        return `${start.format("MMM D")} – ${start.clone().add(6, "days").format("MMM D, YYYY")}`;
    };

    const daysSummary = (override: ISupplier_Delivery_Schedule_Override): string =>
        (override.Days || [])
            .map(
                (day) =>
                    `${moment(String(day.delivery_date).slice(0, 10), "YYYY-MM-DD").format("ddd, MMM D")} (${intl.formatMessage({ id: "order_by" })} ${moment(
                        String(day.hard_deadline_date).slice(0, 10),
                        "YYYY-MM-DD"
                    ).format("ddd, MMM D")} ${format12h(day.hard_deadline_time)})`
            )
            .join("; ");

    return (
        <div className="DeliveryScheduleOverridesSection mt-4">
            <div className="d-flex justify-content-between align-items-center">
                <OwTitle title="schedule_exceptions" tag="h5" leftIconName="fad fa-calendar-times" />
                <OwButton
                    title="add_exception"
                    leftIconClass="fas fa-plus"
                    fn={() => setTarget({ mode: "new" })}
                    className="btn-outline-primary btn-sm"
                    disabled={!!target}
                />
            </div>
            <OwLabel title="help_text_schedule_exceptions" className="text-muted" />

            {target && (
                <DeliveryScheduleOverrideForm
                    schedules={props.schedules}
                    existingOverride={target.mode === "edit" ? target.override : undefined}
                    onSaved={onSaved}
                    onCancel={() => setTarget(null)}
                />
            )}

            {isLoading ? (
                <LoadingResults />
            ) : overrides.length === 0 ? (
                <OwLabel title="no_schedule_exceptions_yet" className="text-muted mt-2 d-block" />
            ) : (
                <div className="table-responsive mt-2">
                    <table className="table table-sm align-middle">
                        <thead>
                            <tr>
                                <th>
                                    <OwLabel title="week_of" noBottomMargin={true} />
                                </th>
                                <th>
                                    <OwLabel title="applies_to" noBottomMargin={true} />
                                </th>
                                <th>
                                    <OwLabel title="delivery_days" noBottomMargin={true} />
                                </th>
                                <th>
                                    <OwLabel title="note" noBottomMargin={true} />
                                </th>
                                <th className="text-end">
                                    <OwLabel title="actions" noBottomMargin={true} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {overrides.map((override) => (
                                <tr key={override.guid}>
                                    <td>{weekLabel(override)}</td>
                                    <td>{override.Delivery_Schedule?.name || intl.formatMessage({ id: "all_templates" })}</td>
                                    <td>
                                        {override.no_delivery ? (
                                            <OwBadge title="no_deliveries_this_week" bgColor="bg-secondary" />
                                        ) : (
                                            daysSummary(override)
                                        )}
                                    </td>
                                    <td>{override.note || ""}</td>
                                    <td className="text-end">
                                        <OwButton
                                            centerIconClass="fas fa-pencil"
                                            fn={() => setTarget({ mode: "edit", override })}
                                            className="btn-outline-primary btn-sm me-2"
                                            ariaLabel="edit exception"
                                            disabled={!!target}
                                        />
                                        <OwButton
                                            centerIconClass="fas fa-trash"
                                            fn={() => setPendingDelete(override)}
                                            className="btn-outline-danger btn-sm"
                                            ariaLabel="delete exception"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                isOpen={!!pendingDelete}
                className="modalContentClass"
                overlayClassName="modalOverlayClass"
                contentLabel="confirm delete exception"
                shouldCloseOnOverlayClick={!isDeleting}
                onRequestClose={() => !isDeleting && setPendingDelete(null)}
            >
                <div className="p-3">
                    <OwTitle title="delete_exception" tag="h5" leftIconName="fad fa-exclamation-triangle" />
                    <OwLabel title="delete_exception_warning" className="mt-2 d-block" />
                    {pendingDelete && <div className="fw-bold mt-2">{weekLabel(pendingDelete)}</div>}
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <OwButton title="cancel" fn={() => setPendingDelete(null)} className="btn-secondary" disabled={isDeleting} />
                        <OwButton title="delete" fn={confirmDelete} className="btn-danger" disabled={isDeleting} />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DeliveryScheduleOverridesSection;
